import { datadogRum } from '@datadog/browser-rum';
import { useEffect, useState } from 'react';

const SCRIPT_ID = 'recaptcha-enterprise';

const useRecaptchaValidation = (siteKey: string) => {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let cancelled = false;

    const markReady = () => {
      if (window.grecaptcha?.enterprise) {
        window.grecaptcha.enterprise.ready(() => {
          if (!cancelled) setIsReady(true);
        });
      }
    };

    if (window.grecaptcha?.enterprise) {
      markReady();
      return () => {
        cancelled = true;
      };
    }

    let script = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
      script.async = true;
      document.head.appendChild(script);
    }

    const handleError = () => {
      datadogRum.addError(new Error('reCAPTCHA enterprise script failed to load'), {
        operation: 'recaptcha_script_load',
      });
      // Remove the failed element so a later mount injects a fresh script and retries.
      script?.remove();
    };

    // addEventListener (not .onload) so every hook instance is notified, including
    // ones that mounted while a script injected by an earlier mount was still loading.
    script.addEventListener('load', markReady);
    script.addEventListener('error', handleError);

    // The script may have finished loading between the check above and attaching
    // the listener; grecaptcha is defined synchronously by the script, so re-check.
    markReady();

    return () => {
      cancelled = true;
      script.removeEventListener('load', markReady);
      script.removeEventListener('error', handleError);
    };
  }, [siteKey]);

  const getToken = async (action: string): Promise<string | null> => {
    if (!window.grecaptcha?.enterprise) return null;
    try {
      const token = await window.grecaptcha.enterprise.execute(siteKey, { action });
      return token;
    } catch (err) {
      console.error('reCAPTCHA error:', err);
      datadogRum.addError(err instanceof Error ? err : new Error('reCAPTCHA execute failed'), {
        operation: 'recaptcha_execute',
        action,
      });
      return null;
    }
  };

  return { getToken, isReady };
};

export default useRecaptchaValidation;
